import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import './Videos.css';
import { getLabeledVideoUrl } from '../lib/videoUtils';
import { deleteVideoRecord } from '../lib/deleteVideo';
import BodyPatternTicks from '../components/BodyPatternTicks';

const Videos = ({ user }) => {
  const [videos, setVideos] = useState([]);
  const [horses, setHorses] = useState([]);
  const [feedback, setFeedback] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedHorse, setSelectedHorse] = useState('all');
  const [expandedVideo, setExpandedVideo] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState({});
  const [savingFeedback, setSavingFeedback] = useState(null);
  const [deletingVideo, setDeletingVideo] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (user) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [videosRes, horsesRes, feedbackRes] = await Promise.all([
        supabase
          .from('videos')
          .select('*, analysis_reports(*), horses(name)')
          .eq('owner_id', user.id)
          .order('upload_date', { ascending: false }),
        supabase
          .from('horses')
          .select('id, name')
          .eq('owner_id', user.id)
          .order('name'),
        supabase
          .from('user_feedback')
          .select('*')
          .eq('user_id', user.id),
      ]);
      if (videosRes.error) throw videosRes.error;
      if (horsesRes.error) throw horsesRes.error;
      setVideos(videosRes.data || []);
      setHorses(horsesRes.data || []);
      const fbMap = {};
      (feedbackRes.data || []).forEach(f => { fbMap[f.video_id] = f; });
      setFeedback(fbMap);
    } catch (err) {
      console.error('Error loading videos:', err);
    }
    setLoading(false);
  };

  const initFeedbackForm = (videoId) => {
    const existing = feedback[videoId];
    setFeedbackForm(prev => ({
      ...prev,
      [videoId]: existing ? {
        agrees_with_ai: existing.agrees_with_ai,
        user_assessment: existing.user_assessment || 'unsure',
        known_conditions: existing.known_conditions || '',
        horse_history: existing.horse_history || '',
        notes: existing.notes || '',
      } : {
        agrees_with_ai: null,
        user_assessment: 'unsure',
        known_conditions: '',
        horse_history: '',
        notes: '',
      }
    }));
  };

  const updateFeedbackField = (videoId, field, value) => {
    setFeedbackForm(prev => ({
      ...prev,
      [videoId]: { ...prev[videoId], [field]: value }
    }));
  };

  const saveFeedback = async (videoId) => {
    const form = feedbackForm[videoId];
    if (!form || form.agrees_with_ai === null) return;
    setSavingFeedback(videoId);

    const data = {
      video_id: videoId,
      user_id: user.id,
      agrees_with_ai: form.agrees_with_ai,
      user_assessment: form.user_assessment,
      known_conditions: form.known_conditions || null,
      horse_history: form.horse_history || null,
      notes: form.notes || null,
      updated_at: new Date().toISOString(),
    };

    const existing = feedback[videoId];
    let error;
    if (existing) {
      ({ error } = await supabase.from('user_feedback').update(data).eq('id', existing.id));
    } else {
      ({ error } = await supabase.from('user_feedback').insert(data));
    }

    if (error) {
      console.error('Feedback error:', error);
      showToast('❌ Error saving feedback');
    } else {
      showToast('✅ Feedback saved — thank you!');
      await loadData();
    }
    setSavingFeedback(null);
  };

  const deleteVideo = async (video) => {
    if (!window.confirm(`Delete ${video.filename}? This cannot be undone.`)) return;
    setDeletingVideo(video.id);
    try {
      await deleteVideoRecord(supabase, video);
      setVideos((prev) => prev.filter((v) => v.id !== video.id));
      setFeedback((prev) => {
        const next = { ...prev };
        delete next[video.id];
        return next;
      });
      showToast(`Deleted ${video.filename}`);
      await loadData();
    } catch (err) {
      console.error('Delete error:', err);
      showToast(`Could not delete: ${err.message}`);
    }
    setDeletingVideo(null);
  };

  const filteredVideos = selectedHorse === 'all'
    ? videos
    : selectedHorse === 'unlinked'
      ? videos.filter(v => !v.horse_id)
      : videos.filter(v => v.horse_id === selectedHorse);

  const groupedByHorse = {};
  filteredVideos.forEach(v => {
    const horseName = v.horses?.name || 'Unlinked Videos';
    if (!groupedByHorse[horseName]) groupedByHorse[horseName] = [];
    groupedByHorse[horseName].push(v);
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getScoreColor = (score) => {
    if (score >= 96) return '#22c55e';
    if (score >= 92) return '#86efac';
    if (score >= 85) return '#eab308';
    if (score >= 75) return '#f97316';
    return '#ef4444';
  };

  if (loading) {
    return <div className="videos-container"><div className="loading-spinner">Loading your videos...</div></div>;
  }

  return (
    <div className="videos-container">
      {toast && <div className="videos-toast">{toast}</div>}

      <div className="videos-header">
        <div>
          <h1>📹 My Videos</h1>
          <p>{videos.length} video{videos.length !== 1 ? 's' : ''} uploaded</p>
        </div>
        <Link to="/analyze" className="upload-btn">+ Upload Video</Link>
      </div>

      {horses.length > 0 && (
        <div className="horse-filter">
          <button className={`filter-btn ${selectedHorse === 'all' ? 'active' : ''}`} onClick={() => setSelectedHorse('all')}>
            All ({videos.length})
          </button>
          {horses.map(h => {
            const count = videos.filter(v => v.horse_id === h.id).length;
            return (
              <button key={h.id} className={`filter-btn ${selectedHorse === h.id ? 'active' : ''}`} onClick={() => setSelectedHorse(h.id)}>
                🐴 {h.name} ({count})
              </button>
            );
          })}
          {videos.some(v => !v.horse_id) && (
            <button className={`filter-btn ${selectedHorse === 'unlinked' ? 'active' : ''}`} onClick={() => setSelectedHorse('unlinked')}>
              Unlinked ({videos.filter(v => !v.horse_id).length})
            </button>
          )}
        </div>
      )}

      {filteredVideos.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📹</span>
          <h3>No videos yet</h3>
          <p>Upload your first video to get started with AI stride analysis</p>
          <Link to="/analyze" className="upload-btn">Upload Video</Link>
        </div>
      ) : (
        Object.entries(groupedByHorse).map(([horseName, horseVideos]) => (
          <div key={horseName} className="horse-group">
            <h2 className="horse-group-title">
              🐴 {horseName}
              <span className="video-count">{horseVideos.length} video{horseVideos.length !== 1 ? 's' : ''}</span>
            </h2>
            <div className="video-list">
              {horseVideos.map(video => {
                const report = video.analysis_reports?.[0];
                const isExpanded = expandedVideo === video.id;
                const fb = feedback[video.id];
                const form = feedbackForm[video.id];

                return (
                  <div key={video.id} className="video-item">
                    <div className="video-item-main" onClick={() => {
                      const newId = isExpanded ? null : video.id;
                      setExpandedVideo(newId);
                      if (newId) initFeedbackForm(video.id);
                    }}>
                      <div className="video-icon">🎬</div>
                      <div className="video-details">
                        <h4>{video.filename}</h4>
                        <p className="video-meta">
                          {formatDate(video.upload_date)}
                          {video.file_size_bytes ? ` • ${formatSize(video.file_size_bytes)}` : ''}
                          {video.gait_type && video.gait_type !== 'unknown' ? ` • ${video.gait_type}` : ''}
                          {fb && <> • {fb.agrees_with_ai ? '✅ Reviewed' : '❌ Flagged'}</>}
                        </p>
                      </div>
                      <div className="video-status">
                        {report ? (
                          <span className="score-pill" style={{ backgroundColor: getScoreColor(report.soundness_score) }}>
                            {report.soundness_score}/100
                          </span>
                        ) : (
                          <span className="status-pill pending">⏳ Pending</span>
                        )}
                      </div>
                      <span className={`expand-arrow ${isExpanded ? 'expanded' : ''}`}>▸</span>
                    </div>

                    {isExpanded && (
                      <div className="video-expanded">
                        {report && (
                        <div className="report-summary">
                          <div className="report-row">
                            <span className="report-label">Assessment</span>
                            <span className="report-value">{report.is_abnormal ? '⚠️ Abnormal' : '✅ Normal'}</span>
                          </div>
                          <div className="report-row">
                            <span className="report-label">Soundness Score</span>
                            <span className="report-value" style={{ color: getScoreColor(report.soundness_score) }}>{report.soundness_score}/100</span>
                          </div>
                          <div className="report-row">
                            <span className="report-label">Severity</span>
                            <span className="report-value">{report.overall_severity}</span>
                          </div>
                        </div>
                        )}

                        {report?.findings?.length > 0 && (
                          <div className="findings-list">
                            <h5>Findings</h5>
                            {report.findings.map((f, i) => (
                              <div key={i} className="finding-item">
                                <span className={`severity-dot ${f.severity || 'mild'}`} />
                                <div>
                                  <strong>{f.title}</strong>
                                  <p>{f.description}</p>
                                  {f.recommendation && <p className="recommendation">💡 {f.recommendation}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="label-video-container" style={{ marginBottom: 12 }}>
                          <video controls src={getLabeledVideoUrl(video)} className="label-video"
                            onLoadedMetadata={(e) => { e.target.playbackRate = 0.5; }} />
                        </div>

                        <BodyPatternTicks videoId={video.id} user={user} />

                        {/* User Feedback Section */}
                        {form && (
                          <div className="feedback-section">
                            <h5>🗣️ Your Feedback</h5>
                            <p className="feedback-subtitle">Help improve the AI — tell us what you see</p>

                            <div className="feedback-question">
                              <p>Do you agree with the AI's assessment?</p>
                              <div className="feedback-buttons">
                                <button
                                  className={`fb-btn ${form.agrees_with_ai === true ? 'selected agree' : ''}`}
                                  onClick={() => updateFeedbackField(video.id, 'agrees_with_ai', true)}
                                >✅ Yes, looks right</button>
                                <button
                                  className={`fb-btn ${form.agrees_with_ai === false ? 'selected disagree' : ''}`}
                                  onClick={() => updateFeedbackField(video.id, 'agrees_with_ai', false)}
                                >❌ No, I disagree</button>
                              </div>
                            </div>

                            {form.agrees_with_ai === false && (
                              <div className="feedback-question">
                                <p>What do you think the correct assessment is?</p>
                                <div className="feedback-buttons">
                                  {['normal', 'abnormal', 'unsure'].map(a => (
                                    <button key={a} className={`fb-btn ${form.user_assessment === a ? 'selected' : ''}`}
                                      onClick={() => updateFeedbackField(video.id, 'user_assessment', a)}>
                                      {a === 'normal' ? '✅ Normal' : a === 'abnormal' ? '⚠️ Abnormal' : '🤷 Unsure'}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="feedback-question">
                              <p>Any known conditions for this horse?</p>
                              <textarea
                                value={form.known_conditions}
                                onChange={(e) => updateFeedbackField(video.id, 'known_conditions', e.target.value)}
                                placeholder="e.g., Previous suspensory injury left front, arthritis in hocks, recently trimmed..."
                                rows={2}
                              />
                            </div>

                            <div className="feedback-question">
                              <p>Recent history (anything the AI should know)?</p>
                              <textarea
                                value={form.horse_history}
                                onChange={(e) => updateFeedbackField(video.id, 'horse_history', e.target.value)}
                                placeholder="e.g., Had shoes pulled 2 weeks ago, changed feed, increased workload..."
                                rows={2}
                              />
                            </div>

                            <div className="feedback-question">
                              <p>Additional notes</p>
                              <textarea
                                value={form.notes}
                                onChange={(e) => updateFeedbackField(video.id, 'notes', e.target.value)}
                                placeholder="Anything else you notice about the movement..."
                                rows={2}
                              />
                            </div>

                            <button
                              className="save-feedback-btn"
                              onClick={() => saveFeedback(video.id)}
                              disabled={form.agrees_with_ai === null || savingFeedback === video.id}
                            >
                              {savingFeedback === video.id ? 'Saving...' : (fb ? '💾 Update Feedback' : '💾 Submit Feedback')}
                            </button>
                          </div>
                        )}
                        <div className="video-actions">
                          <button
                            className="action-link delete-btn"
                            onClick={(e) => { e.stopPropagation(); deleteVideo(video); }}
                            disabled={deletingVideo === video.id}
                          >
                            {deletingVideo === video.id ? 'Deleting…' : 'Delete Video'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default Videos;
